import React, { useState, useMemo, useEffect } from 'react';
import {
    Building2,
    Search,
    Printer,
    UserPlus,
    CalendarCheck,
    PenSquare,
    Plus,
    AlertCircle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { buildingTypes, defaultUnitForm } from '../data/unitData';
import StatusBadge from '../components/StatusBadge';
import MetricCard from '../components/MetricCard';
import Toast from '../components/Toast';
import UnitFormPage from '../components/UnitFormPage';
import axios from 'axios';

const RentalUnitDashboard = () => {
    const [units, setUnits] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedBuilding, setSelectedBuilding] = useState("");
    const [selectedBuildingType, setSelectedBuildingType] = useState("All");
    const [selectedPropertyType, setSelectedPropertyType] = useState("-");
    const [selectedUnitType, setSelectedUnitType] = useState("-");
    const [selectedStatus, setSelectedStatus] = useState("-");
    const [searchTerm, setSearchTerm] = useState("");
    const [highlightedRowId, setHighlightedRowId] = useState(1);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [formMode, setFormMode] = useState("add");
    const [formState, setFormState] = useState(defaultUnitForm);
    const [toastMessage, setToastMessage] = useState(null);
    const [buildings, setBuildings] = useState([]);

    const navigate = useNavigate();

    useEffect(() => {
        fetchBuildings();
    }, []);

    useEffect(() => {
        if (selectedBuilding) {

            fetchUnits();
        }
    }, [selectedBuilding, selectedPropertyType, selectedStatus, selectedUnitType]);

    const fetchBuildings = async () => {
        const apiPaths = JSON.parse(localStorage.getItem("apiPaths") || "[]");
        const companyCode = localStorage.getItem("companyCode") || "-";
        try {
            const res = await axios.get(`${apiPaths[0]?.API_CONFIG}/api/CRMTaskMainListFilter/${companyCode}/BUILDING/-/-/-/-/-/1900-01-01/1900-01-01/-/-/-`);
            const data = Array.isArray(res.data) ? res.data : [];
            setBuildings(data);
            if (data.length > 0 && !selectedBuilding) {
                setSelectedBuilding(data[0].BuildingID);
            }
        } catch (err) {
            console.error('Failed to fetch buildings:', err);
        }
    };

    const fetchUnits = async () => {
        const apiPaths = JSON.parse(localStorage.getItem("apiPaths") || "[]");
        const companyCode = localStorage.getItem("companyCode") || "-";

        try {
            setLoading(true);
            setError(null);

            let url;
            url = `${apiPaths[0]?.API_CONFIG}/api/CRMTaskMainListFilter/${companyCode}/UNITBUILDING/${selectedPropertyType}/-/${selectedStatus}/${selectedBuilding}/-/1900-01-01/1900-01-01/-/${selectedUnitType}/1`;
            // https://localhost:7063/api/CRMTaskMainListFilter/SUNRISEWEB/UNITBUILDING/Apartment/-/Vacant/12/-/1900-01-01/1900-01-01/Freshly%20painted/2BHK/1


            const { data } = await axios.get(url);
            setUnits(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("Failed to fetch units:", err);
            setError(err.response?.data?.message || err.message || "Failed to fetch units");
            setUnits([]);
        } finally {
            setLoading(false);
        }
    };

    const showToast = (msg) => {
        setToastMessage(msg);
    };

    const closeToast = () => {
        setToastMessage(null);
    };

    const onTenantRegistration = () => {
        showToast("Directing user to tenant registration schema onboarder...");
        navigate('/tenantRegister');
    };

    const handleSaveUnit = (formData) => {
        setIsFormOpen(false);
        fetchUnits();
        showToast(`Unit ${formMode === "edit" ? "updated" : "created"} successfully!`);
    };

    const handleOpenForm = (mode, data) => {
        setFormMode(mode);
        if (mode === "add") {
            const fallbackId = `${selectedBuilding || buildings[0]?.BuildingID || "P001"}-${Math.floor(100 + Math.random() * 900)}`;
            setFormState({
                ...defaultUnitForm,
                unit_id: fallbackId,
                UnitCode: fallbackId
            });
        } else {
            const mappedData = {
                id: data.UnitID,
                unit_id: data.UnitID,
                unit_code: data.UnitCode,
                unit_number: data.UnitNo,
                unit_name: data.UnitName,
                building_id: data.BuildingID,
                block_tower: data.BlockName,
                wing: data.Wing,
                floor: data.FloorNo,
                property_type: data.PropertyType,
                unit_type: data.UnitType,
                category: data.Category,
                usageType: data.UsageType,
                bedrooms: data.Bedrooms,
                bathrooms: data.Bathrooms,
                maid_room: data.MaidRoom,
                balcony: data.Balcony || data.HasBalcony,
                terrace: data.Terrace,
                store_room: data.StoreRoom,
                parking_count: data.ParkingCount,
                parking_no: data.ParkingNo,
                floor_area: data.FloorArea,
                balcony_area: data.BalconyArea,
                common_area: data.CommonArea,
                total_area: data.TotalArea,
                area_unit: data.AreaUnit,
                monthly_rent: data.MonthlyRent,
                current_rent: data.AnnualRent,
                security_deposit: data.SecurityDeposit,
                service_charge: data.ServiceCharge,
                payment_frequency: data.RentFrequency,
                status: data.UnitStatus,
                availability_date: data.AvailabilityDate ? data.AvailabilityDate.split('T')[0] : '',
                electricity_account: data.ElectricityAccount,
                water_account: data.WaterAccount,
                gas_account: data.GasAccount,
                dewa_no: data.DEWANo,
                makani_no: data.MakaniNo,
                has_central_ac: data.HasCentralAC,
                has_kitchen: data.HasKitchen,
                has_furnished: data.HasFurnished,
                has_internet: data.HasInternet,
                income_account: data.IncomeAccount,
                deposit_account: data.DepositAccount,
                expense_account: data.ExpenseAccount,
                cost_center: data.CostCenter,
                notes: data.Remarks,
                rera_unit_no: data.RERAUnitNo,
                ejari_unit_no: data.EjariUnitNo,
                title_deed_no: data.TitleDeedNo,
                plot_no: data.PlotNo,
                unit_view: data.UnitView,
                chiller_type: data.ChillerType,
                cooling_provider: data.CoolingProvider,
                municipality_fee_applicable: data.MunicipalityFeeApplicable,
                vat_applicable: data.VATApplicable,
                rental_commission: data.RentalCommission,
                renewal_notice_days: data.RenewalNoticeDays,
            };
            console.log("Data:", data)
            console.log("mappedData:", mappedData)

            setFormState(mappedData);
        }
        setIsFormOpen(true);
    };

    const handleCloseForm = () => {
        setIsFormOpen(false);
    };

    const metrics = useMemo(() => {
        const total = units.length;
        const vacant = units.filter(u => u.UnitStatus === "Vacant" || u.status === "OPEN").length;
        const occupied = units.filter(u => u.UnitStatus === "Occupied" || u.status === "OCCUPIED").length;
        const maintenance = units.filter(u => u.UnitStatus === "Maintenance" || u.status === "MAINTENANCE").length;
        const totalValue = units.reduce((acc, curr) => acc + parseFloat(curr.MonthlyRent || curr.current_rent || 0), 0);
        return { total, vacant, occupied, maintenance, totalValue };
    }, [units]);

    const filteredUnits = useMemo(() => {
        if (!searchTerm) return units;

        return units.filter(unit => {
            const unitId = String(unit.UnitID || "");
            const buildingId = String(unit.BuildingID || "").toLowerCase();
            const location = String(unit.BlockName || "");
            const unitType = String(unit.UnitType || "");

            return unitId.toLowerCase().includes(searchTerm.toLowerCase()) ||
                buildingId.includes(searchTerm.toLowerCase()) ||
                location.toLowerCase().includes(searchTerm.toLowerCase()) ||
                unitType.toLowerCase().includes(searchTerm.toLowerCase());
        });
    }, [units, searchTerm]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50/50">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-slate-600 font-medium">Loading units...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50/50">
                <div className="text-center max-w-md mx-auto p-8 bg-white rounded-2xl border border-slate-200 shadow-sm">
                    <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-slate-800 mb-2">Failed to Load Units</h3>
                    <p className="text-slate-600 mb-4">{error}</p>
                    <button
                        onClick={() => window.location.reload()}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm rounded-xl transition-all"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    if (isFormOpen) {
        return (
            <>
                <UnitFormPage
                    onClose={handleCloseForm}
                    onSave={handleSaveUnit}
                    mode={formMode}
                    initialData={formState}
                />
                {toastMessage && (
                    <Toast message={toastMessage} onClose={closeToast} />
                )}
            </>
        );
    }

    return (
        <div className="min-h-screen flex flex-col bg-slate-50/50 text-slate-800">
            <div className="mt-6 text-gray-700 rounded-2xl p-4 flex flex-col md:flex-row items-center justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-slate-900 tracking-tight">Rental Units Dashboard</h2>
                </div>
                <div className="flex gap-2.5 items-center justify-center">
                    <button
                        onClick={onTenantRegistration}
                        className="w-full sm:w-auto px-5 py-2.5 bg-slate-800 hover:bg-slate-700 active:scale-95 text-slate-200 hover:text-white font-semibold text-xs uppercase tracking-wider rounded-xl transition-all flex items-center justify-center gap-2 border border-slate-700"
                    >
                        <UserPlus className="w-4 h-4" />
                        Tenant Registration
                    </button>
                    {/* <button
                        onClick={() => showToast("Opening Lease Reservation Workflow Panel...")}
                        className="w-full sm:w-auto px-5 py-2.5 bg-slate-800 hover:bg-slate-700 active:scale-95 text-slate-200 hover:text-white font-semibold text-xs uppercase tracking-wider rounded-xl transition-all flex items-center justify-center gap-2 border border-slate-700"
                    >
                        <CalendarCheck className="w-4 h-4" />
                        New Booking
                    </button> */}
                    <button
                        onClick={() => handleOpenForm("add")}
                        className="w-full sm:w-auto px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 active:scale-95 text-white font-bold text-xs uppercase tracking-wider rounded-xl shadow-md shadow-emerald-500/10 transition-all flex items-center justify-center gap-2"
                    >
                        <Plus className="w-4 h-4" />
                        New Unit Entry
                    </button>
                </div>
            </div>

            <section className="py-4">
                <div className="mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                        <MetricCard title="Total Units" value={metrics.total} color="slate" />
                        <MetricCard title="Open (Vacant)" value={metrics.vacant} color="emerald" />
                        <MetricCard title="Occupied" value={metrics.occupied} color="blue" />
                        <MetricCard title="In Maintenance" value={metrics.maintenance} color="amber" />
                        <MetricCard title="Total Projected Rent" value={metrics.totalValue} gradient />
                    </div>
                </div>
            </section>

            <main className="flex-1 w-full mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col">
                <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-sm mb-6">
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                        <div className="grid grid-cols-1 sm:grid-cols-6 gap-4 flex-1">
                            <div>
                                <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-1.5">Building ID</label>
                                <div className="relative">
                                    <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-400">
                                        <Building2 className="w-3.5 h-3.5" />
                                    </span>
                                    <select
                                        value={selectedBuilding}
                                        onChange={(e) => setSelectedBuilding(e.target.value)}
                                        className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/10 focus:border-blue-500 transition-all cursor-pointer"
                                    >
                                        {buildings.map(b => (
                                            <option key={b.BuildingID} value={b.BuildingID}>{b.BuildingID} ({b.BuildingName})</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-1.5">Building Category</label>
                                <div className="relative">
                                    <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-400">
                                        <Building2 className="w-3.5 h-3.5" />
                                    </span>
                                    <select
                                        value={selectedBuildingType}
                                        onChange={(e) => setSelectedBuildingType(e.target.value)}
                                        className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/10 focus:border-blue-500 transition-all cursor-pointer"
                                    >
                                        {buildingTypes.map(type => (
                                            <option key={type} value={type}>{type}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-1.5">Property Type</label>
                                <div className="relative">
                                    <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-400">
                                        <AlertCircle className="w-3.5 h-3.5" />
                                    </span>
                                    <select
                                        value={selectedPropertyType}
                                        onChange={(e) => setSelectedPropertyType(e.target.value)}
                                        className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/10 focus:border-blue-500 transition-all cursor-pointer"
                                    >
                                        {/* - Apartment, Villa, Office, Shop, Warehouse */}
                                        <option value="-">All Types</option>
                                        <option value="Apartment">Apartment</option>
                                        <option value="Villa">Villa</option>
                                        <option value="Office">Office</option>
                                        <option value="Shop">Shop</option>
                                        <option value="Warehouse">Warehouse</option>
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-1.5">Unit Status</label>
                                <div className="relative">
                                    <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-400">
                                        <AlertCircle className="w-3.5 h-3.5" />
                                    </span>
                                    <select
                                        value={selectedStatus}
                                        onChange={(e) => setSelectedStatus(e.target.value)}
                                        className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/10 focus:border-blue-500 transition-all cursor-pointer"
                                    >
                                        {/* Vacant / Occupied / Reserved / Maintenance / Sold */}
                                        <option value="-">All Statuses</option>
                                        <option value="Vacant">Vacant</option>
                                        <option value="Occupied">Occupied</option>
                                        <option value="Reserved">Reserved</option>
                                        <option value="Maintenance">Maintenance</option>
                                        <option value="Sold">Sold</option>
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-1.5">Unit Type</label>
                                <div className="relative">
                                    <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-400">
                                        <AlertCircle className="w-3.5 h-3.5" />
                                    </span>
                                    <select
                                        value={selectedUnitType}
                                        onChange={(e) => setSelectedUnitType(e.target.value)}
                                        className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/10 focus:border-blue-500 transition-all cursor-pointer"
                                    >
                                        {/* Studio, 1BHK, 2BHK, Office... */}
                                        <option value="-">All Types</option>
                                        <option value="Office">Office</option>
                                        <option value="Studio">Studio</option>
                                        <option value="1BHK">1BHK</option>
                                        <option value="2BHK">2BHK</option>

                                    </select>
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-1.5">Global Unit Search</label>
                                <div className="relative">
                                    <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-400">
                                        <Search className="w-3.5 h-3.5" />
                                    </span>
                                    <input
                                        type="text"
                                        placeholder="Search by unit ID, Block"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/10 focus:border-blue-500 transition-all"
                                    />
                                </div>
                            </div>
                        </div>
                        {/* <div className="flex gap-2">
                            <button
                                onClick={() => window.print()}
                                className="px-5 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold text-sm rounded-xl transition-all flex items-center gap-2 border border-slate-200"
                            >
                                <Printer className="w-4 h-4" />
                                Print Index
                            </button>
                        </div> */}
                    </div>
                </div>

                <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm flex-1 flex flex-col overflow-hidden">
                    <div className="flex-1 overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead className='bg-slate-200/60'>
                                <tr className="bg-slate-50/50 border-b border-slate-100 text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                                    {/* <th className="py-4 px-6 text-center w-12">Sl No</th>
                                    <th className="py-4 px-4">Building ID</th>
                                    <th className="py-4 px-4">Building Name</th>
                                    <th className="py-4 px-4">Building Category</th>
                                    <th className="py-4 px-4">Unit ID</th>
                                    <th className="py-4 px-4">Unit Type</th>
                                    <th className="py-4 px-4">Unit Status</th>
                                    <th className="py-4 px-4">Remarks</th>
                                    <th className="py-4 px-4">Block</th>
                                    <th className="py-4 px-4">Rent</th>
                                    <th className="py-4 px-4">Available From</th>
                                    <th className="py-4 px-4 text-center">Actions</th> */}


                                    <th className="py-4 px-6 text-center w-12">Sl No</th>
                                    <th className="py-4 px-4">Unit ID</th>
                                    <th className="py-4 px-4">Unit No</th>
                                    <th className="py-4 px-4">Unit Name</th>
                                    <th className="py-4 px-4">Property Type</th>
                                    <th className="py-4 px-4">Unit Type</th>
                                    <th className="py-4 px-4">Category</th>
                                    <th className="py-4 px-4">Status</th>
                                    <th className="py-4 px-4">Bedrooms</th>
                                    <th className="py-4 px-4">Bathrooms</th>
                                    <th className="py-4 px-4">Monthly Rent</th>
                                    <th className="py-4 px-4">Available From</th>
                                    <th className="py-4 px-4 text-center">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {filteredUnits.length === 0 ? (
                                    <tr>
                                        <td colSpan="11" className="py-12 text-center text-sm text-slate-400 font-medium">
                                            No assets match current configuration query.
                                        </td>
                                    </tr>
                                ) : (
                                    filteredUnits.map((unit, idx) => {
                                        // const buildingId = unit.BuildingID || unit.building_id;
                                        // const buildingName = unit.BuildingName;
                                        // const buildingType = unit.Category;
                                        // const unitId = unit.UnitID || unit.unit_id;
                                        // const unitType = unit.UnitType || unit.type;
                                        // const status = unit.UnitStatus || unit.status;
                                        // const details = unit.Remarks || unit.details;
                                        // const location = unit.BlockName;
                                        // const currentRent = unit.MonthlyRent || 0;
                                        // const availableDate = unit.AvailabilityDate;


                                        const unitId = unit.UnitID || unit.unit_id;
                                        const unitNo = unit.UnitNo;
                                        const unitName = unit.UnitName;
                                        const propertyType = unit.PropertyType;
                                        const unitType = unit.UnitType;
                                        const category = unit.Category;
                                        const unitStatus = unit.UnitStatus;
                                        const bedrooms = unit.Bedrooms;
                                        const bathrooms = unit.Bathrooms;
                                        const monthlyRent = unit.MonthlyRent;
                                        const availabilityDate = unit.AvailabilityDate;

                                        const statusMap = {
                                            "Vacant": "OPEN",
                                            "Occupied": "OCCUPIED",
                                            "Maintenance": "MAINTENANCE"
                                        };
                                        const normalizedStatus = statusMap[unitStatus] || unitStatus;

                                        return (
                                            <tr
                                                key={unit.id || idx}
                                                onClick={() => setHighlightedRowId(unit.id)}
                                                className={`group hover:bg-blue-50/30 cursor-pointer transition-all ${highlightedRowId === unit.id ? 'bg-blue-50/70 font-medium' : ''}`}
                                            >
                                                <td className="py-4 px-6 text-center text-xs text-slate-400">
                                                    <span className="text-blue-600">
                                                        {idx + 1}
                                                    </span>
                                                </td>
                                                {/* <td className="py-4 px-4 text-sm font-semibold text-slate-700">{buildingId}</td>
                                                <td className="py-4 px-4 text-sm text-slate-600">
                                                    {buildingName || '-'}
                                                </td>
                                                <td className="py-4 px-4 text-sm text-slate-600">
                                                    {buildingType || '-'}
                                                </td>

                                                <td className="py-4 px-4 text-sm font-semibold text-slate-700"> {unitId}</td>
                                                <td className="py-4 px-4 text-sm text-slate-600">{unitType}</td>
                                                <td className="py-4 px-4 text-sm">
                                                    <StatusBadge status={normalizedStatus} />
                                                </td>
                                                <td className="py-4 px-4 text-sm text-slate-500 max-w-[150px] truncate" title={details}>{details || '-'}</td>
                                                <td className="py-4 px-4 text-sm text-slate-600">{location || '-'}</td>

                                                <td className="py-4 px-4 text-sm text-slate-600">{currentRent}</td>
                                                <td className="py-4 px-4 text-sm text-slate-500">
                                                    {availableDate ? (isNaN(Date.parse(availableDate)) ? availableDate : new Date(availableDate).toLocaleDateString()) : '-'}
                                                </td> */}

                                                <td className="py-4 px-4 text-sm text-slate-600 text-center">
                                                    {unitId || '-'}
                                                </td>
                                                <td className="py-4 px-4 text-sm text-slate-600 text-center">
                                                    {unitNo || '-'}
                                                </td>
                                                <td className="py-4 px-4 text-sm text-slate-600 text-center">
                                                    {unitName || '-'}
                                                </td>
                                                <td className="py-4 px-4 text-sm text-slate-600 text-center">
                                                    {propertyType || '-'}
                                                </td>
                                                <td className="py-4 px-4 text-sm text-slate-600 text-center">
                                                    {unitType || '-'}
                                                </td>
                                                <td className="py-4 px-4 text-sm text-slate-600 text-center">
                                                    {category || '-'}
                                                </td>
                                                <td className="py-4 px-4 text-sm text-slate-600 text-center">
                                                    {unitStatus || '-'}
                                                </td>
                                                <td className="py-4 px-4 text-sm text-slate-600 text-center">
                                                    {bedrooms || '-'}
                                                </td>
                                                <td className="py-4 px-4 text-sm text-slate-600 text-center">
                                                    {bathrooms || '-'}
                                                </td>
                                                <td className="py-4 px-4 text-sm text-slate-600 text-center">
                                                    {monthlyRent || '-'}
                                                </td>
                                                <td className="py-4 px-4 text-sm text-slate-600 text-center">
                                                    {availabilityDate ? (isNaN(Date.parse(availabilityDate)) ? availabilityDate : new Date(availabilityDate).toLocaleDateString()) : '-'}

                                                </td>

                                                <td className="py-4 px-4 text-center flex gap-2">
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); handleOpenForm("edit", unit); }}
                                                        className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 active:scale-95 text-white font-bold text-[10px] uppercase tracking-wider rounded-lg transition-all flex items-center gap-1.5 mx-auto shadow-sm"
                                                    >
                                                        <PenSquare className="w-3 h-3" />
                                                        Edit
                                                    </button>
                                                    <button
                                                        // onClick={(e) => { e.stopPropagation(); handleOpenForm("edit", unit); }}
                                                        className="px-3 py-1.5 bg-gray-600 hover:bg-gray-700 active:scale-95 text-white font-bold text-[10px] uppercase tracking-wider rounded-lg transition-all flex items-center gap-1.5 mx-auto shadow-sm"
                                                    >

                                                        <Printer className="w-4 h-4" />
                                                        Print
                                                    </button>

                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </main>

            {toastMessage && (
                <Toast message={toastMessage} onClose={closeToast} />
            )}
        </div>
    );
};

export default RentalUnitDashboard;
